/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-11 21:18:04
 * @LastEditors: linguang linguang@nextmar.com
 * @LastEditTime: 2025-04-15 20:37:42
 * @FilePath: /zeelool-nextjs/src/components/AutoComplete/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
/*
 * @Description: AutoComplete组件，封装MUI的Autocomplete组件
 */

/**
 * react-hook-form学到的：如果外部使用方使用了onChange，那么当我使用
 * useWatch监听时，会监听不到，因为onChange会覆盖掉useWatch的监听
 * 另外所有的表单应该有onChange、value、defaultValue这些表单设置
 */
'use client';

import {
  AutocompleteInputChangeReason,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  TextField,
} from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import React, { forwardRef, useCallback } from 'react';
import { Control, Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import { colors } from '@/theme/zeelool.theme';
import useHydratedWhenEnabled from '../useHydratedWhenEnabled';
import type { FloatingLabelVariant } from '../FormInput';

// 支持字符串或对象类型的选项
export type OptionType =
  | string
  | { label: string; value: string; color?: string; [key: string]: any };

export type AutoCompleteProps<
  T = OptionType,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> & {
  label?: string;
  placeholder?: string;
  formItemClassName?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  required?: boolean;
  helperText?: string;
  /**
   * react-hook-form 字段名
   */
  name?: string;
  /**
   * react-hook-form 控制器
   */
  control?: Control<any>;
  /**
   * react-hook-form 验证规则
   */
  rules?: RegisterOptions;
  /**
   * 标题和输入框的布局方式，默认inline，也就是标题和输入框融为一体
   * 如果设置为vertical，那么标题和输入框会垂直排列
   */
  layout?: 'inline' | 'vertical' | 'horizontal';

  /** 是否限制文本宽度变成省略号 */
  optionTextIsEllipsis?: boolean;

  /**
   * SSR 首帧为空、客户端从缓存恢复为非空值时，挂载后同步修复浮动 Label 和 outline 缺口。
   */
  shrinkLabelWhenFilled?: boolean;

  /** Label 的浮动方式，支付页移动端使用输入框内部浮动标签。 */
  floatingLabelVariant?: FloatingLabelVariant;

  /** Raises the option popper above a parent modal when needed. */
  popperZIndex?: number;

  onInputChange?: (
    event: React.ChangeEvent<any>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
};

/**
 * AutoComplete组件，封装MUI的Autocomplete组件
 */
function AutoComplete<
  T = OptionType,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<HTMLDivElement>,
) {
  const {
    label,
    placeholder,
    variant = 'outlined',
    required = false,
    helperText,
    name,
    control,
    rules,
    layout = 'inline',
    optionTextIsEllipsis = false,
    shrinkLabelWhenFilled = false,
    floatingLabelVariant = 'notched',
    popperZIndex,
    onInputChange,
    formItemClassName = layout === 'horizontal' ? 'mb-0' : 'mb-[24px]', // Select框marginTop自己有8px，所以规范32px-8px
    ...otherProps
  } = props;

  // 获取表单上下文
  const formContext = useFormContext();
  const isHydrated = useHydratedWhenEnabled(shrinkLabelWhenFilled);

  // 处理失焦事件
  const handleBlur = useCallback(() => {
    // 手动触发验证
    if (formContext && name) {
      setTimeout(() => {
        formContext.trigger(name);
      }, 0);
    }
  }, [formContext, name]);

  // 调试选项样式使用，暂时保留
  // const handleOpen = () => {
  //   setTimeout(() => {
  //     debugger; // 这会在浏览器中触发一个断点
  //   }, 400);
  // };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const shouldRestoreFilledOutline =
          shrinkLabelWhenFilled &&
          isHydrated &&
          field.value !== undefined &&
          field.value !== null &&
          String(field.value).trim() !== '';
        const renderedFieldValue = shrinkLabelWhenFilled && !isHydrated ? null : field.value;

        return (
          <div className={`relative ${formItemClassName}`}>
            {/* 标题 */}
            {layout === 'vertical' && label && (
              <label className="block text-[16px] text-primary-text font-br mb-[12px]">
                {label}
              </label>
            )}
            <MuiAutocomplete
              ref={ref}
              {...otherProps}
              value={renderedFieldValue}
              popupIcon={
                otherProps.disabled ? null : (
                  <CollapseArrowDownOutlined className="text-[16px] text-auxiliary-text" />
                )
              }
              // onOpen={handleOpen}
              slotProps={{
                popper: popperZIndex === undefined ? undefined : { sx: { zIndex: popperZIndex } },
                paper: {
                  elevation: 8,
                  style: {
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    marginTop: '0',
                  },
                },
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: 'auto',
                  },
                  className: 'hide-scrollbar',
                  sx: {
                    '& .MuiAutocomplete-option': {
                      height: '40px',
                      minHeight: '40px',
                      lineHeight: '18px',
                      ...(optionTextIsEllipsis
                        ? {
                            display: 'block !important',
                            width: '100%',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }
                        : {}),
                    },
                    '& .MuiAutocomplete-option.Mui-focused': {
                      backgroundColor: '#F0F3FF',
                    },
                    '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option[aria-selected="true"]:hover':
                      {
                        backgroundColor: '#F0F3FF',
                      },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    ...(shouldRestoreFilledOutline ? { shrink: true } : {}),
                  }}
                  InputProps={{
                    ...params.InputProps,
                    ...(shouldRestoreFilledOutline ? { notched: true } : {}),
                  }}
                  label={layout === 'vertical' ? undefined : label}
                  placeholder={placeholder}
                  variant={variant}
                  required={required}
                  error={!!error}
                  helperText={!error ? helperText : undefined}
                  sx={
                    floatingLabelVariant === 'inside' && label
                      ? {
                          '&.MuiTextField-root': {
                            marginTop: 0,
                          },
                          '& .MuiOutlinedInput-root.MuiInputBase-root': {
                            minHeight: '50px',
                            borderRadius: '6px',
                          },
                          '& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
                            paddingLeft: '0px',
                          },
                          '& .MuiInputLabel-root.MuiFormLabel-root': {
                            top: 0,
                            transform: 'translate(12px, 16.5px) scale(1)',
                            transformOrigin: 'top left',
                            fontSize: '14px',
                            lineHeight: '17px',
                          },
                          '& .MuiInputLabel-root.MuiFormLabel-root.MuiInputLabel-shrink': {
                            transform: 'translate(12px, 6px) scale(1)',
                            fontSize: '12px',
                            lineHeight: '14px',
                          },
                          '& .MuiInputLabel-root.MuiFormLabel-root.Mui-error, & .MuiInputLabel-root.MuiFormLabel-root.Mui-error span':
                            {
                              color: colors.auxiliaryText,
                            },
                          '& .MuiAutocomplete-input': {
                            padding: '21px 12px 6px 12px !important',
                            fontSize: '14px',
                            lineHeight: '17px',
                          },
                          '& .MuiOutlinedInput-notchedOutline legend': {
                            maxWidth: '0 !important',
                          },
                        }
                      : undefined
                  }
                />
              )}
              onBlur={handleBlur}
              onChange={(event, newValue, ...rest) => {
                field.onChange(newValue);
                if (otherProps.onChange) {
                  otherProps.onChange(event, newValue, ...rest);
                }
              }}
              onInputChange={(
                event: React.ChangeEvent<any>,
                value: string,
                reason: AutocompleteInputChangeReason,
              ) => {
                if (onInputChange) {
                  const filteredValue = value.replace(
                    /[^\u0020-\u007E\u00A0-\u00BE\u00C0-\u00FF\u2E80-\uA4CF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFFEF\u0080-\u009F\u2000-\u201F\r\n]/g,
                    '',
                  );
                  field.onChange(filteredValue);

                  onInputChange(event, filteredValue, reason);
                }
              }}
              sx={{
                ...otherProps.sx,
                // 增加 .MuiInputLabel-shrink 可以限制仅占位符移上去的时候才会变红
                '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-error': {
                  color: '#bc1b1b',
                },
                '& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-error span': {
                  color: '#bc1b1b',
                },
              }}
            />
            {/* 使用CSS transitions实现错误信息的动画显示 */}
            <div
              className={`
            absolute left-0 top-full text-error text-[12px] pt-[4px]
            transition-all duration-200 ease-in-out
            ${error ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'}
          `}
            >
              {error?.message}
            </div>
          </div>
        );
      }}
    />
  );
}

export default forwardRef(AutoComplete) as <
  T = OptionType,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.ReactElement;
